import { useEffect } from 'react';
import { suppressGhostClick } from '../utils/dismissFocus';

const DISMISS_DY = 96;
const DISMISS_VEL = 0.55;
const EXIT_MS = 280;

/**
 * Свайп вниз по ручке/шапке sheet → закрытие с анимацией.
 * @param {{ sheetRef: React.RefObject<HTMLElement|null>, handleRef?: React.RefObject<HTMLElement|null>, backdropRef?: React.RefObject<HTMLElement|null>, onDismiss: () => void, enabled?: boolean }} opts
 */
export function useSheetSwipeDismiss({
  sheetRef,
  handleRef,
  backdropRef,
  onDismiss,
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return undefined;
    const sheet = sheetRef?.current;
    if (!sheet) return undefined;
    const handle = handleRef?.current || sheet;

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let dy = 0;
    let dragging = false;
    let axisLocked = false;
    let lastY = 0;
    let lastT = 0;
    let vel = 0;
    let closing = false;

    const backdrop = () => backdropRef?.current || null;

    const clearInline = () => {
      sheet.style.transform = '';
      sheet.style.transition = '';
      sheet.style.opacity = '';
      sheet.classList.remove('is-dragging');
      const bd = backdrop();
      if (bd) {
        bd.style.opacity = '';
        bd.style.transition = '';
      }
    };

    const setDrag = (y) => {
      dy = Math.max(0, y);
      sheet.classList.add('is-dragging');
      sheet.style.transition = 'none';
      sheet.style.transform = `translateY(${dy}px)`;
      const h = Math.max(sheet.offsetHeight, 1);
      const bd = backdrop();
      if (bd) {
        bd.style.transition = 'none';
        bd.style.opacity = String(Math.max(0.15, 1 - dy / (h * 0.85)));
      }
    };

    const snapBack = () => {
      sheet.classList.remove('is-dragging');
      sheet.style.transition =
        'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease';
      sheet.style.transform = 'translateY(0)';
      const bd = backdrop();
      if (bd) {
        bd.style.transition = 'opacity 0.28s ease';
        bd.style.opacity = '1';
      }
      window.setTimeout(clearInline, EXIT_MS);
    };

    const dismissFrom = (fromY) => {
      if (closing) return;
      closing = true;
      sheet.classList.remove('is-dragging');
      const h = Math.max(sheet.offsetHeight, fromY + 48, 240);
      sheet.style.transition =
        'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease';
      sheet.style.transform = `translateY(${h}px)`;
      sheet.style.opacity = '0';
      const bd = backdrop();
      if (bd) {
        bd.style.transition = 'opacity 0.28s ease, visibility 0.28s ease';
        bd.style.opacity = '0';
      }
      suppressGhostClick(EXIT_MS + 160);
      window.setTimeout(() => {
        onDismiss?.();
        clearInline();
        closing = false;
      }, EXIT_MS);
    };

    const onDown = (e) => {
      if (closing) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('button, a, input, textarea, select, [role="button"]')) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      lastY = startY;
      lastT = performance.now();
      dy = 0;
      dragging = false;
      axisLocked = false;
      vel = 0;
      try {
        handle.setPointerCapture(pointerId);
      } catch (_) {
        /* ignore */
      }
    };

    const onMove = (e) => {
      if (pointerId == null || e.pointerId !== pointerId || closing) return;
      const x = e.clientX - startX;
      const y = e.clientY - startY;
      if (!axisLocked) {
        if (Math.abs(x) < 6 && Math.abs(y) < 6) return;
        axisLocked = true;
        if (Math.abs(x) > Math.abs(y) || y < 0) {
          pointerId = null;
          try {
            handle.releasePointerCapture(e.pointerId);
          } catch (_) {
            /* ignore */
          }
          return;
        }
        dragging = true;
      }
      if (!dragging) return;
      e.preventDefault();
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      vel = (e.clientY - lastY) / dt;
      lastY = e.clientY;
      lastT = now;
      setDrag(y);
    };

    const onUp = (e) => {
      if (pointerId == null || e.pointerId !== pointerId) return;
      pointerId = null;
      try {
        handle.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
      if (!dragging) {
        clearInline();
        return;
      }
      dragging = false;
      if (dy >= DISMISS_DY || vel >= DISMISS_VEL) dismissFrom(dy);
      else snapBack();
    };

    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove, { passive: false });
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);

    return () => {
      handle.removeEventListener('pointerdown', onDown);
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      clearInline();
    };
  }, [sheetRef, handleRef, backdropRef, onDismiss, enabled]);
}
